import React, { useRef, useState } from "react";
import { uploadTokenLogo } from "../../../helpers/fakebackend_helper";
import { LOGO_TYPES, MAX_LOGO_BYTES } from "../constants";
import { describeError } from "../utils";
import { TokenLogo } from "./Badges";
import { inputCls, labelCls, btnGhost } from "./Panel";

/**
 * Logo input: upload a PNG/JPEG/WEBP (stored public-read by BiorAdmin-Backend,
 * returns a URL) or paste an https URL. Either way the form keeps a URL.
 */
export default function LogoField({ value, symbol, onChange, error, disabled }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploadError("");
    if (!LOGO_TYPES.includes(file.type)) {
      setUploadError("The logo must be a PNG, JPEG or WEBP image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setUploadError("The logo must be 512 KB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadTokenLogo(file);
      if (res && typeof res.url === "string") onChange(res.url);
      else setUploadError("The logo could not be stored. Try again, or paste an https image URL.");
    } catch (err) {
      setUploadError(describeError(err, "The logo could not be stored. Try again, or paste an https image URL."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={labelCls} htmlFor="tl-logo-url">Logo</label>
      <div className="flex items-start gap-3">
        <TokenLogo url={value} symbol={symbol} size={48} />
        <div className="flex-1 min-w-0 space-y-2">
          <input
            id="tl-logo-url"
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className={inputCls}
            disabled={disabled || uploading}
            aria-invalid={Boolean(error)}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              className={btnGhost}
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={disabled || uploading}
            >
              {uploading ? "Uploading..." : "Upload image"}
            </button>
            {value && !uploading && (
              <button type="button" className="text-xs text-gray-400 hover:text-gray-200" onClick={() => onChange("")} disabled={disabled}>
                Remove
              </button>
            )}
            <span className="text-xs text-gray-500">PNG, JPEG or WEBP, up to 512 KB. Square images look best.</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onFile}
            data-testid="tl-logo-file"
          />
          {(uploadError || error) && <div className="text-xs text-red-300">{uploadError || error}</div>}
        </div>
      </div>
    </div>
  );
}
